import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { GPA_LOGO } from "@/lib/assets";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, CheckCircle2 } from "lucide-react";

export default function RegistrationRespond() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [ask, setAsk] = useState("");
  const [name, setName] = useState("");
  const [reply, setReply] = useState("");
  const [state, setState] = useState("loading"); // loading | form | done | error
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState("error"); setError("This link is missing its token."); return; }
    api.get("/registration/respond-info", { params: { token } })
      .then(({ data }) => { setAsk(data.message || ""); setName(data.name || ""); setState("form"); })
      .catch((err) => { setState("error"); setError(formatApiError(err.response?.data?.detail) || err.message); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/registration/respond-info", { token, message: reply });
      setState("done");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-12">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-4 flex items-center justify-center rounded-lg bg-[#003057] px-3 py-2 mx-auto w-fit">
          <img src={GPA_LOGO} alt="GPA+" className="h-8 w-auto" />
        </div>
        {state === "loading" && <div className="py-6 text-center"><Loader2 className="mx-auto h-8 w-8 animate-spin text-[#003057]" /></div>}
        {state === "error" && (
          <div className="text-center" data-testid="respond-error">
            <h1 className="font-heading text-xl font-extrabold text-[#003057]">Link unavailable</h1>
            <p className="mt-2 text-sm text-slate-600">{error}</p>
            <Link to="/login"><Button className="mt-6 w-full rounded-full bg-[#003057] py-5 font-bold">Go to sign in</Button></Link>
          </div>
        )}
        {state === "form" && (
          <form onSubmit={submit} data-testid="respond-form">
            <h1 className="font-heading text-2xl font-extrabold text-[#003057]">More information needed</h1>
            <p className="mt-1 text-sm text-slate-500">Hi {name || "there"}, an administrator asked:</p>
            <p className="mt-3 rounded-lg bg-slate-100 px-3 py-2 text-sm text-[#003057]">{ask}</p>
            <Textarea className="mt-4" rows={5} value={reply} onChange={(e) => setReply(e.target.value)}
              placeholder="Type your response…" required data-testid="respond-message" />
            {error && <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
            <Button type="submit" disabled={busy} className="mt-4 w-full rounded-full bg-[#003057] py-5 font-bold hover:bg-[#004275]" data-testid="respond-submit">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send response"}
            </Button>
          </form>
        )}
        {state === "done" && (
          <div className="text-center" data-testid="respond-done">
            <CheckCircle2 className="mx-auto h-12 w-12 text-[#5fa114]" />
            <h1 className="mt-3 font-heading text-2xl font-extrabold text-[#003057]">Response sent</h1>
            <p className="mt-2 text-sm text-slate-600">Thank you — your response has been sent to the reviewing administrator.</p>
            <Link to="/login"><Button className="mt-6 w-full rounded-full bg-[#003057] py-5 font-bold">Go to sign in</Button></Link>
          </div>
        )}
      </div>
    </div>
  );
}
